import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Colors } from "../../utils/utils.colors";
import { routes } from "../../utils/utils.routes";
import { NavItem } from "./NavItem";
import { Logs, Search, X, ChevronDown, ChevronRight } from "lucide-react";
import { apiGet } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { pickLocalized } from "../../utils/locale";

const MenuIcon = Logs as any;
const SearchIcon = Search as any;
const CloseIcon = X as any;

export const MainNavigation: React.FC = () => {
  const { lang, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [faculties, setFaculties] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Charger les facultés
  useEffect(() => {
    apiGet("/faculties")
      .then((d) => setFaculties(d.rows || []))
      .catch(() => {});
  }, []);

  // Fermer le menu au changement de route
  useEffect(() => {
    setIsMenuOpen(false);
    document.body.style.overflow = "";
  }, [location.pathname]);

  // Bloquer le scroll quand le menu est ouvert
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Fermer en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const toggleSubmenu = (key: string) => {
    setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const submitMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`${routes.SEARCH}?q=${encodeURIComponent(search.trim())}`);
      setSearch("");
      closeMenu();
    }
  };

  // Construire les items du menu principal (mêmes que desktop)
  const facultySubItems = faculties.map((fac) => ({
    label: pickLocalized(fac, "name", lang),
    href: `${routes.FACULTIES}/${fac.slug}`,
    subItems: (fac.filieres || []).map((fil: any) => ({
      label: pickLocalized(fil, "name", lang),
      href: `${routes.FACULTIES}/${fac.slug}#filiere-${fil.slug}`,
    })),
  }));

  const menuItems = [
    { key: "home", label: t.nav.home, href: routes.HOME },
    {
      key: "faculties",
      label: t.nav.faculties,
      href: routes.FACULTIES,
      subItems: facultySubItems,
    },
    { key: "centers", label: t.nav.centers, href: routes.CENTRES },
    {
      key: "planning",
      label: t.nav.planningMenu,
      href: routes.SCHEDULES,
      subItems: [
        { label: t.nav.schedules, href: routes.SCHEDULES },
        { label: t.nav.events, href: routes.EVENTS },
        { label: t.nav.tenders, href: routes.TENDERS },
      ],
    },
    { key: "articles", label: t.nav.articles, href: routes.ARTICLES },
    { key: "contact", label: t.nav.contact, href: routes.CONTACTS },
  ];

  // Composant pour afficher un item avec accordéon
  const MobileMenuItem = ({ item, depth = 0 }: { item: any; depth?: number }) => {
    const hasSub = item.subItems && item.subItems.length > 0;
    const isOpen = openSubmenus[item.key || item.label];
    const paddingLeft = depth * 16 + 16;

    return (
      <li className="mobile-menu-item" style={{ listStyle: "none" }}>
        <div
          className={`mobile-menu-link-wrapper ${hasSub ? "has-sub" : ""}`}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingLeft: `${paddingLeft}px`,
          }}
        >
          {item.href ? (
            <a
              href={item.href}
              onClick={(e) => {
                e.preventDefault();
                navigate(item.href);
                closeMenu();
              }}
              className="mobile-menu-link"
              style={{
                flex: 1,
                padding: "12px 0",
                color: Colors.darkColor,
                textDecoration: "none",
                fontWeight: 500,
                fontSize: "1rem",
              }}
            >
              {item.label}
            </a>
          ) : (
            <span
              style={{
                flex: 1,
                padding: "12px 0",
                color: Colors.darkColor,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              {item.label}
            </span>
          )}
          {hasSub && (
            <button
              onClick={() => toggleSubmenu(item.key || item.label)}
              className="mobile-submenu-toggle"
              style={{
                background: "none",
                border: "none",
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                color: Colors.darkColor,
              }}
            >
              {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
          )}
        </div>
        {hasSub && isOpen && (
          <ul className="mobile-submenu" style={{ paddingLeft: 0, margin: 0 }}>
            {item.subItems.map((sub: any, idx: number) => (
              <MobileMenuItem key={idx} item={{ ...sub, key: `${item.key}-sub-${idx}` }} depth={depth + 1} />
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav
      style={{ fontStyle: "normal", fontWeight: "500" }}
      className="main-navigation-wrapper"
      aria-label="Main"
    >
      {/* Bouton hamburger (mobile seulement) */}
      <button
        data-testid="mobile-menu-toggle"
        className="mobile-menu-toggle"
        onClick={toggleMenu}
        aria-label="Open menu"
        style={{
          display: "none", // masqué par défaut, visible via media query CSS
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "8px",
        }}
      >
        <MenuIcon size={24} color={Colors.darkColor} />
      </button>

      {/* Menu desktop (affiché normalement sur desktop) */}
      <div className="desktop-navigation">
        <ul className="wp-block-navigation__container is-responsive items-justified-center wp-block-navigation">
          <NavItem href={routes.HOME} label={t.nav.home} color={Colors.darkColor} />
          <NavItem
            href={routes.FACULTIES}
            label={t.nav.faculties}
            color={Colors.darkColor}
            hasPlus
            subItems={facultySubItems}
          />
          <NavItem href={routes.CENTRES} label={t.nav.centers} color={Colors.darkColor} />
          <NavItem
            href={routes.SCHEDULES}
            label={t.nav.planningMenu}
            color={Colors.darkColor}
            hasPlus
            subItems={[
              { label: t.nav.schedules, href: routes.SCHEDULES },
              { label: t.nav.events, href: routes.EVENTS },
              { label: t.nav.tenders, href: routes.TENDERS },
            ]}
          />
          <NavItem href={routes.ARTICLES} label={t.nav.articles} color={Colors.darkColor} />
          <NavItem href={routes.CONTACTS} label={t.nav.contact} color={Colors.darkColor} />
        </ul>
      </div>

      {/* Menu mobile overlay latéral (off-canvas) */}
      <div
        className={`mobile-menu-overlay ${isMenuOpen ? "open" : ""}`}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
          zIndex: 2000,
          visibility: isMenuOpen ? "visible" : "hidden",
          opacity: isMenuOpen ? 1 : 0,
          transition: "opacity 0.3s ease, visibility 0.3s",
        }}
        onClick={closeMenu}
      >
        <div
          ref={menuRef}
          className="mobile-menu-sidebar"
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "min(85%, 360px)",
            backgroundColor: "white",
            boxShadow: "-5px 0 25px rgba(0,0,0,0.2)",
            transform: isMenuOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1)",
            display: "flex",
            flexDirection: "column",
            zIndex: 2001,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* En-tête du menu mobile */}
          <div
            className="mobile-menu-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
              borderBottom: "1px solid #eee",
            }}
          >
            <span style={{ fontWeight: "bold", fontSize: "1.2rem", color: Colors.primaryColor }}>
              Menu
            </span>
            <button
              onClick={closeMenu}
              style={{ background: "none", border: "none", cursor: "pointer" }}
              aria-label="Close menu"
            >
              <CloseIcon size={24} />
            </button>
          </div>

          {/* Barre de recherche dans le menu mobile */}
          <form onSubmit={submitMobileSearch} className="mobile-search-form" style={{ margin: "16px" }}>
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="submit">
              <SearchIcon size={18} />
            </button>
          </form>

          {/* Liste des items du menu (accordéon) */}
          <ul
            className="mobile-menu-list"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "0 0 20px 0",
              margin: 0,
            }}
          >
            {menuItems.map((item) => (
              <MobileMenuItem key={item.key} item={item} depth={0} />
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};